import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { useLanguage } from "../../contexts/LanguageContext";
import LanguageMenu from "../../components/UI/LanguageMenu";
import { FiArrowLeft } from "react-icons/fi";
import AuthMap from "./AuthMap";
import { Note } from "./shared";

const Canvas = styled.main`
  min-height: 100svh;
  padding: clamp(18px, 3vw, 48px);
  display: grid;
  place-items: center;
  background:
    radial-gradient(ellipse at 15% 40%, #ff985b08, transparent 55%),
    ${(p) => p.theme.colors.background};
`;
const Frame = styled.div`
  width: 100%;
  max-width: 1120px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: 24px;
  overflow: hidden;
  background: ${(p) => p.theme.colors.surface};
  box-shadow: 0 30px 100px #0005;
  @media (max-width: 850px) {
    grid-template-columns: 1fr;
    max-width: 480px;
  }
`;
const Story = styled.aside`
  padding: 40px;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 40px;
  background:
    radial-gradient(ellipse at 90% 75%, #ff985b18, transparent 65%),
    linear-gradient(150deg, #20232b, #101217);
  border-right: 1px solid ${(p) => p.theme.colors.border};
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    opacity: 0.22;
    background-image:
      linear-gradient(#ffffff12 1px, transparent 1px),
      linear-gradient(90deg, #ffffff12 1px, transparent 1px);
    background-size: 48px 48px;
    mask-image: linear-gradient(transparent, #000);
    pointer-events: none;
  }
  > * {
    position: relative;
  }
  h2 {
    font-size: clamp(32px, 3.3vw, 42px);
    letter-spacing: -0.04em;
    line-height: 1.18;
    font-weight: 750;
    max-width: 390px;
    margin: 0;
  }
  h2 span {
    color: ${(p) => p.theme.colors.primary};
  }
  p {
    max-width: 330px;
    margin: 0;
  }
  @media (max-width: 850px) {
    display: none;
  }
`;
const FormSide = styled.div`
  padding: clamp(24px, 4vw, 48px);
  display: grid;
  gap: 24px;
  align-content: center;
  background: linear-gradient(155deg, #1a1d24, #14171c);
  h1 {
    font-size: clamp(25px, 3vw, 30px);
    line-height: 1.2;
    letter-spacing: -0.04em;
    margin: 8px 0;
  }
  label {
    font-size: 16px;
    font-weight: 500;
  }
  input {
    font-size: 17px;
    font-weight: 400;
    min-height: 52px;
  }
  button,
  a {
    font-size: 16px;
  }
  form {
    display: grid;
    gap: 18px;
  }
  a {
    color: ${(p) => p.theme.colors.primary};
  }
  a:hover {
    text-decoration: underline;
  }
  .auth-brand {
    display: none;
  }
  @media (max-width: 850px) {
    .auth-brand {
      display: flex;
    }
  }
`;
const Brand = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  width: fit-content;
  min-height: 40px;
  font-size: 15px;
  color: ${(p) => p.theme.colors.textSecondary};
  &:hover {
    color: ${(p) => p.theme.colors.text};
  }
  img {
    height: 34px;
    width: auto;
  }
  svg {
    width: 18px;
    height: 18px;
  }
`;
export const AuthFooter = styled.div`
  text-align: center;
  font-size: 16px;
  color: ${(p) => p.theme.colors.textMuted};
  a {
    font-weight: 600;
    margin-left: 5px;
  }
`;
export const PasswordBox = styled.div`
  position: relative;
  input {
    padding-right: 48px;
  }
  button {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 36px;
    height: 36px;
    display: grid;
    place-items: center;
    color: ${(p) => p.theme.colors.textMuted};
    border-radius: 7px;
    &:hover {
      background: ${(p) => p.theme.colors.surfaceHover};
      color: ${(p) => p.theme.colors.text};
    }
  }
`;
export const InlineCode = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;
  @media (max-width: 370px) {
    grid-template-columns: 1fr;
  }
`;
export default function AuthShell({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  const { t } = useLanguage();
  return (
    <Canvas>
      <Frame>
        <Story>
          <Brand to="/" aria-label={t("auth.back_home")}>
            <FiArrowLeft />
            {t("auth.back_home")}
          </Brand>
          <div>
            <h2>
              {t("auth.story_1")}
              <br />
              <span>{t("auth.story_2")}</span>
            </h2>
            <AuthMap />
          </div>
        </Story>
        <FormSide>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <Brand className="auth-brand" to="/" aria-label={t("auth.back_home")}>
              <FiArrowLeft />
              {t("auth.back_home")}
            </Brand>
            <LanguageMenu />
          </div>
          <header>
            <h1>{title}</h1>
            {description && <Note>{description}</Note>}
          </header>
          {children}
        </FormSide>
      </Frame>
    </Canvas>
  );
}
